import React, { useState } from "react";
import { backendApi } from "./api/backendApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Upload, Search, Folder } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";

// Import components
import DocumentUpload from "../components/documents/DocumentUpload";
import DocumentCard from "../components/documents/DocumentCard";

// ---------------------------------------------------------------------
//  THEME TOKENS — matches the flat editorial surface used across HRMS
//  (kept in sync with the tokens in EmployeeDashboard.jsx)
// ---------------------------------------------------------------------
const T = {
  green: '#1A3C2B',
  greenDeep: '#122B1F',
  greenTint: '#EDF3EF',
  bg: '#F6F6F3',
  card: '#FFFFFF',
  line: '#E7E6E0',
  lineSoft: '#F0EFEA',
  ink: '#15181C',
  body: '#3F4650',
  muted: '#6B7280',
  faint: '#9CA3AF',
};

// ---------------------------------------------------------------------
//  PRIMITIVES (mirrors the primitives used on the Dashboard page)
// ---------------------------------------------------------------------
const Panel = ({ title, icon: Icon, note, children }) => (
  <section className="hrms-card flex flex-col">
    {title && (
      <div className="flex items-center gap-2 px-5 pt-4 pb-3">
        {Icon && <Icon className="w-[15px] h-[15px] flex-shrink-0" style={{ color: T.green }} strokeWidth={2} />}
        <h3 className="text-[15px] font-bold" style={{ color: T.ink }}>{title}</h3>
        {note && <span className="hrms-note truncate">{note}</span>}
      </div>
    )}
    <div className="px-5 pb-5 flex-1 min-w-0">{children}</div>
  </section>
);

export default function Documents() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");
  const queryClient = useQueryClient();

  const { data: documents = [] } = useQuery({
    queryKey: ['documents'],
    queryFn: () => backendApi.entities.Document.list(),
    initialData: [],
  });

  const createDocumentMutation = useMutation({
    mutationFn: (docData) => backendApi.entities.Document.create(docData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setShowUpload(false);
      toast.success("Document uploaded successfully!");
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (id) => backendApi.entities.Document.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success("Document deleted successfully!");
    },
  });

  // Category shelf — a unique-to-this-page way to browse documents by folder
  // instead of another bar/donut widget. Falls back gracefully across a few
  // likely field names, and buckets anything unlabeled under "General".
  const categoryOf = (doc) => doc.category || doc.document_type || doc.type || "General";
  const categoryCounts = documents.reduce((acc, doc) => {
    const c = categoryOf(doc);
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const categories = Object.keys(categoryCounts).sort();

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "All" || categoryOf(doc) === activeCategory;
    return (!searchTerm || matchesSearch) && matchesCategory;
  });

  return (
    <div className="p-6 md:p-8" style={{ background: T.bg, minHeight: '100%' }}>
      {/* Header row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-[19px] font-bold tracking-tight" style={{ color: T.ink }}>Documents</h2>
          <p className="text-[12px] mt-0.5" style={{ color: T.muted }}>Manage company documents and policies</p>
        </div>
        <button onClick={() => setShowUpload(true)} className="hrms-btn-primary inline-flex items-center gap-2">
          <Upload className="w-3.5 h-3.5" strokeWidth={2} />
          Upload Document
        </button>
      </div>

      {/* Upload Form */}
      <AnimatePresence>
        {showUpload && (
          <DocumentUpload
            onClose={() => setShowUpload(false)}
            onSuccess={(data) => createDocumentMutation.mutate(data)}
            isLoading={createDocumentMutation.isLoading}
          />
        )}
      </AnimatePresence>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: T.faint }} />
        <input
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full h-11 pl-11 pr-4 rounded-xl text-[13px] focus:outline-none"
          style={{ border: `1px solid ${T.line}`, background: T.card, color: T.ink }}
        />
      </div>

      {/* Category shelf */}
      {documents.length > 0 && (
        <Panel title="Browse by Category" icon={Folder}>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory("All")}
              className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors"
              style={
                activeCategory === "All"
                  ? { background: T.green, color: '#fff' }
                  : { background: T.greenTint, color: T.green }
              }
            >
              All <span className="opacity-70">· {documents.length}</span>
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors"
                style={
                  activeCategory === cat
                    ? { background: T.green, color: '#fff' }
                    : { background: T.lineSoft, color: T.body }
                }
              >
                {cat} <span className="opacity-70">· {categoryCounts[cat]}</span>
              </button>
            ))}
          </div>
        </Panel>
      )}

      {/* Documents Grid */}
      <div className="mt-6">
        {filteredDocuments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredDocuments.map((doc) => (
              <DocumentCard
                key={doc.id}
                document={doc}
                onDelete={(id) => {
                  if (window.confirm("Are you sure you want to delete this document?")) {
                    deleteDocumentMutation.mutate(id);
                  }
                }}
              />
            ))}
          </div>
        ) : (
          <div className="hrms-card p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: T.lineSoft }} strokeWidth={1.5} />
            <h3 className="text-[15px] font-bold" style={{ color: T.ink }}>
              {documents.length === 0 ? "No Documents Yet" : "No documents match your filters"}
            </h3>
            <p className="text-[12.5px] mt-1 mb-5" style={{ color: T.muted }}>
              {documents.length === 0
                ? "Start by uploading your first document"
                : "Try a different search term or category"}
            </p>
            {documents.length === 0 && (
              <button onClick={() => setShowUpload(true)} className="hrms-btn-primary inline-flex items-center gap-2">
                <Upload className="w-3.5 h-3.5" strokeWidth={2} />
                Upload First Document
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}